/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Input, message, Modal, ModalProps } from 'antd';
import { useIntl } from 'react-intl';
import ModuleTenderHammerNoColor from '~src/assets/nav-icons/folder-tender-hammer-no-color.svg';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { fadeInKeyframes } from '~src/styles/animations';
import { useEffect, useRef, useState } from 'react';
import { queryCache, useMutation } from 'react-query';
import { patchTendersRegistrationInfo } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { IconPopup } from '~components/popups-modals/DetailsLayouts/components';
import TextArea from 'antd/lib/input/TextArea';

export const MainModalWindow = (
  props: Pick<ModalProps, 'visible' | 'onOk' | 'onCancel'> & {
    item: {
      id?: string;
      title?: string;
      share_title?: string;
      reference?: string;
      description?: string;
      projectId?: string;
      tenderId?: string;
    };
  }
) => {
  const intl = useIntl();
  const [reference, setReference] = useState('');
  const [title, setTitle] = useState('');
  const [publicTitle, setPublicTitle] = useState('');
  const [description, setDescription] = useState('');

  const [isModalLoading, setIsModalLoading] = useState(false);

  //Init once per "open session", not on every refetch
  const didInitRef = useRef(false);

  useEffect(() => {
    // When modal closes -> allow init next time it opens
    if (!props.visible) {
      didInitRef.current = false;
      return;
    }

    // When modal is visible -> initialize ONLY ONCE for this open session
    if (props.visible && !didInitRef.current) {
      const nextReference = props.item.reference
        ? props.item.reference.replace(/^#+/, '') // removes leading #
        : '';

      setReference(nextReference);
      setTitle(props.item.title ?? '');
      setPublicTitle(props.item.share_title ?? '');
      setDescription(props.item.description ?? '');

      didInitRef.current = true;
    }
  }, [
    props.visible,
    props.item.reference,
    props.item.title,
    props.item.share_title,
    props.item.description,
  ]);

  const [doSaveTenderMainData] = useMutation(patchTendersRegistrationInfo, {
    onMutate: () => {
      setIsModalLoading(true);
    },
    onError: (err) => {
      appLog('err -> ', err);
      message.error(intl.formatMessage({ id: 'notification-toggle-error' }));
      setIsModalLoading(false);
    },
    onSuccess: () => {
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
      queryCache.invalidateQueries('tendersRegistrationCoreTenders');
      queryCache.invalidateQueries('tenders');
      setIsModalLoading(false);

      props.onOk &&
        props.onOk(
          (null as unknown) as React.MouseEvent<HTMLElement, MouseEvent>
        );
    },
  });

  const Title = () => (
    <div>
      <NavIcon
        customStyle={{ width: '45px', height: '22px' }}
        icon={ModuleTenderHammerNoColor}
      />
      {intl.formatMessage({ id: 'tender-details-edit-main' })}
    </div>
  );

  return (
    <Modal
      confirmLoading={isModalLoading}
      title={<Title />}
      visible={props.visible}
      okText={intl.formatMessage({ id: 'save' })}
      onOk={() => {
        if (!props.item.projectId || !props.item.tenderId) return;
        doSaveTenderMainData({
          projectId: props.item.projectId,
          tenderId: props.item.tenderId,
          share_title: publicTitle,
          reference: reference,
          description,
          title,
        } as Parameters<typeof doSaveTenderMainData>[0]);
      }}
      maskClosable={false}
      onCancel={props.onCancel}>
      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'reference-tag' })}
        <IconPopup
          headerId={'project-tender-edit-details-reference-help-head'}
          messageId={'project-tender-edit-details-reference-help-body'}
        />
      </label>
      <Input
        autoCapitalize="none"
        addonBefore="#"
        value={reference}
        onChange={({ target: { value } }) => {
          // if user types/pastes "#abc" -> store "abc"
          const cleaned = value.replace(/^#+/, '');
          setReference(cleaned);
        }}
      />

      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'title' })}
        <IconPopup
          headerId={'project-tender-edit-details-title-help-head'}
          messageId={'project-tender-edit-details-title-help-body'}
        />
      </label>
      <Input
        autoCapitalize="words"
        value={title}
        onChange={({ target: { value } }) =>
          setTitle(value.charAt(0).toUpperCase() + value.slice(1))
        }
      />

      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'tender-create-public-title' })}
        <IconPopup
          headerId={'project-tender-edit-details-publictitle-help-head'}
          messageId={'project-tender-edit-details-publictitle-help-body'}
        />
      </label>
      <Input
        autoCapitalize="words"
        value={publicTitle}
        onChange={({ target: { value } }) =>
          setPublicTitle(value.charAt(0).toUpperCase() + value.slice(1))
        }
      />

      <label style={{ fontSize: '14px' }}>
        {intl.formatMessage({ id: 'description' })}
        <IconPopup
          headerId={'project-tender-edit-details-details-help-head'}
          messageId={'project-tender-edit-details-details-help-body'}
        />
      </label>
      <div css={descriptionTextareaWrapperStyle}>
        <TextArea
          className="main-modal-description-textarea"
          style={{ resize: 'vertical' }}
          rows={6}
          placeholder={''}
          value={description}
          onChange={({ target: { value } }) => setDescription(value)}
          onBlur={() => {
            if (description)
              setDescription(
                description.charAt(0).toUpperCase() + description.slice(1)
              );
          }}
        />
        <span
          style={{
            position: 'absolute',
            bottom: 5,
            right: 24,
            fontSize: 16,
            color: '#888',
            pointerEvents: 'none',
            userSelect: 'none',
            lineHeight: 1,
          }}>
          ↕
        </span>
      </div>
    </Modal>
  );
};

const descriptionTextareaWrapperStyle = css`
  position: relative;

  .main-modal-description-textarea {
    scrollbar-width: auto;
  }

  .main-modal-description-textarea::-webkit-scrollbar {
    width: 16px;
    height: 16px;
  }

  .main-modal-description-textarea::-webkit-scrollbar-track {
    background: transparent;
  }

  .main-modal-description-textarea::-webkit-scrollbar-thumb {
    background-color: rgba(42, 49, 79, 0.65);
    border: 5px solid transparent;
    border-radius: 999px;
    background-clip: content-box;
  }

  .main-modal-description-textarea::-webkit-scrollbar-thumb:hover {
    background-color: rgba(42, 49, 79, 0.8);
  }
`;
